import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AdminOnboardingCard } from '../../onboarding';
import { CreditCard, Shield, Link2, Check, User, Zap } from 'lucide-react';
import { useAdminData, ErrorBox, LoadingBlock } from '../../components/admin/AdminKit';
import AdminChatAgentsCard from '../../components/admin/AdminChatAgentsCard'; // WO-56

// WO-36 — below the onboarding card this page is READ-ONLY, and now says so.
// Removed: "Edit Plan", the admin edit button, "Add Admin User", "Configure"
// and the three notification switches — none of them did anything. Prices come
// from the table checkout charges (services/stripe_service.PLAN_PRICES), and the
// integrations show whether their keys are really present.

export default function AdminSettings() {
  const { data, loading, error, reload } = useAdminData('/api/analytics/settings/overview');
  const plans = data?.plans || [];
  const adminUsers = data?.admin_users || [];
  const integrations = data?.integrations || {};
  const good = (s) => s === 'live key' || s === 'key present';

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold">Admin Settings</h1>
        <p className="text-muted-foreground">Platform configuration</p>
      </div>

      {/* WO-33 — the onboarding switch, demo link and per-account reset. */}
      <AdminOnboardingCard />

      {/* WO-56 — the AI chat assistants: switches, limits, knowledge, report texts. */}
      <AdminChatAgentsCard />

      {loading && <LoadingBlock testid="admin-settings-loading" />}
      {error && <ErrorBox message={error} onRetry={reload} />}
      {data && (
        <Tabs defaultValue="plans" className="w-full">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="plans">Plans & Pricing</TabsTrigger>
            <TabsTrigger value="permissions">Admins</TabsTrigger>
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="space-y-4">
            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" />Subscription plans</CardTitle>
                <CardDescription>
                  Read-only — the prices checkout charges. Changing a price is a code change (and a new price in Stripe).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {plans.map((plan) => (
                    <div key={plan.id} className="p-4 rounded-lg border-2 border-muted bg-muted/30" data-testid={`plan-${plan.id}`}>
                      <h3 className="font-bold mb-2">{plan.name}</h3>
                      <p className="text-2xl font-bold">
                        ${plan.price}
                        <span className="text-sm font-normal text-muted-foreground">/mo{plan.per_seat ? ' per seat' : ''}</span>
                      </p>
                      {plan.price_yearly > 0 && (
                        <p className="text-xs text-muted-foreground mb-3">${plan.price_yearly}/year{plan.per_seat ? ' per seat' : ''}</p>
                      )}
                      <ul className="space-y-2 mt-3">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" />{f}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="permissions" className="space-y-4">
            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-primary" />Admin users</CardTitle>
                <CardDescription>People with full admin access. Roles are changed in Users Management (and recorded in the audit trail).</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {adminUsers.map((admin) => (
                  <div key={admin.id} className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center"><User className="w-5 h-5 text-primary" /></div>
                      <div>
                        <p className="font-medium">{admin.name}</p>
                        <p className="text-sm text-muted-foreground">{admin.email}</p>
                      </div>
                    </div>
                    <Badge variant="outline">Admin</Badge>
                  </div>
                ))}
                {adminUsers.length === 0 && <p className="text-center py-8 text-muted-foreground">No admin users found</p>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations" className="space-y-4">
            <Card className="border-0 bg-card/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-primary" />Integrations</CardTitle>
                <CardDescription>Whether each service's key is present on the server (keys are never shown)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(integrations).map(([key, it]) => (
                  <div key={key} className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${good(it.status) ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
                        <Zap className={`w-5 h-5 ${good(it.status) ? 'text-green-500' : 'text-amber-500'}`} />
                      </div>
                      <div>
                        <p className="font-medium capitalize">{key}</p>
                        <p className="text-sm text-muted-foreground">{it.name}</p>
                      </div>
                    </div>
                    <Badge variant={good(it.status) ? 'default' : 'secondary'}>{it.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
